import { SUPPORTED_LANGUAGES, type LanguageCode } from "./languages";

type Json = Record<string, unknown>;

/**
 * Translation files live at `locales/<language>/<namespace>.json`. Each file's content is nested
 * under its file name, so `locales/en/tenants.json` → `t("tenants.…")`. This keeps every area of
 * the app in its own small file (no merge fights) while callers still use one flat `t()`.
 */
export function buildResources(modules: Record<string, unknown>): Record<LanguageCode, Json> {
    const resources = Object.fromEntries(
        SUPPORTED_LANGUAGES.map((l) => [l.code, {} as Json])
    ) as Record<LanguageCode, Json>;

    for (const [path, mod] of Object.entries(modules)) {
        const match = /\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
        if (!match) continue;
        const [, lang, namespace] = match;
        if (!(lang in resources)) continue;
        resources[lang as LanguageCode][namespace] = mod as Json;
    }
    return resources;
}

/** Every dotted key path that holds a string, e.g. `tenants.list.title`. */
export function flattenKeys(obj: unknown, prefix = ""): string[] {
    if (obj === null || typeof obj !== "object") return prefix ? [prefix] : [];
    return Object.entries(obj as Json).flatMap(([k, v]) =>
        typeof v === "string"
            ? [prefix ? `${prefix}.${k}` : k]
            : flattenKeys(v, prefix ? `${prefix}.${k}` : k)
    );
}

export function getByPath(obj: unknown, path: string): string | undefined {
    let cur: unknown = obj;
    for (const part of path.split(".")) {
        if (cur === null || typeof cur !== "object") return undefined;
        cur = (cur as Json)[part];
    }
    return typeof cur === "string" ? cur : undefined;
}

/** `{{name}}` placeholders in a translation, sorted, so languages can be compared. */
export function placeholdersOf(text: string): string[] {
    return [...text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]).sort();
}
